// src/services/questionFactory/providers/chatGptProvider.js
//
// ChatGPT (OpenAI) generation provider.
//
// Phase 2a: real generate() calls go through the
// `question-factory-generate` Supabase Edge Function, which holds the
// OPENAI_API_KEY server-side. The browser only sees pipeline_items rows
// after they've been validated + persisted server-side.
//
// review() is still a Phase 2a stub — adjudication wires up in Phase 2c.

import { supabase } from '../../supabaseClient';
import { ProviderError, normalizeGenerateOpts } from './providerBase';

const PROVIDER_ID   = 'chatgpt';
const DISPLAY_NAME  = 'ChatGPT';
const DEFAULT_MODEL = 'gpt-4o-mini';

/**
 * Phase 2a generation. Requires a pre-created pipeline run.
 *
 * @param {object} opts
 * @param {string} opts.runId          - The pipeline_runs row id (required).
 * @param {string} opts.examId         - 'pmp' | 'pmi-acp' | 'cpmai'.
 * @param {number} opts.count          - Number of drafts to ask for (1–50).
 * @param {string} [opts.domain]       - Optional domain hint.
 * @param {string} [opts.model]        - Override the default model.
 * @param {string} [opts.difficulty]   - 'Easy' | 'Medium' | 'Hard'.
 * @param {string} [opts.extraInstructions]
 * @returns {Promise<{
 *   inserted: number,
 *   failed:   number,
 *   modelUsed: string,
 *   inputTokens: number,
 *   outputTokens: number,
 *   warnings: string[],
 *   items: Array<{ id: string | null, warnings: string[], error?: string }>
 * }>}
 */
async function generate(opts = {}) {
  if (!opts.runId) {
    throw new ProviderError('generate(): runId is required (create a pipeline run first)', { provider: PROVIDER_ID });
  }
  const o = normalizeGenerateOpts(opts);

  const payload = {
    run_id:             opts.runId,
    provider:           PROVIDER_ID,
    exam_id:            o.examId,
    count:              o.count,
    domain:             o.domain,
    model:              opts.model || DEFAULT_MODEL,
    difficulty:         opts.difficulty || null,
    extra_instructions: opts.extraInstructions || null,
  };

  const { data, error } = await supabase.functions.invoke('question-factory-generate', { body: payload });
  if (error) {
    // FunctionsHttpError carries a response we can read for the server's reason.
    let detail = error.message || String(error);
    try {
      if (error.context && typeof error.context.text === 'function') {
        const txt = await error.context.text();
        if (txt) detail = `${detail} — ${txt}`;
      }
    } catch { /* swallow */ }
    throw new ProviderError(`ChatGPT generate failed: ${detail}`, { provider: PROVIDER_ID, cause: error });
  }
  if (!data) {
    throw new ProviderError('ChatGPT generate: empty response from edge function', { provider: PROVIDER_ID });
  }
  if (data.error) {
    throw new ProviderError(`ChatGPT generate: ${data.error}`, { provider: PROVIDER_ID });
  }
  return {
    inserted:     Number(data.items_inserted || 0),
    failed:       Number(data.items_failed   || 0),
    modelUsed:    data.model_used || DEFAULT_MODEL,
    inputTokens:     Number(data.input_tokens  || 0),
    outputTokens:    Number(data.output_tokens || 0),
    estimatedCostUsd: Number(data.estimated_cost_usd || 0),
    warnings:        Array.isArray(data.warnings) ? data.warnings : [],
    items:           Array.isArray(data.items) ? data.items : [],
  };
}

/** Phase 2c stub — adjudication review wires up later. */
async function review(opts = {}) {
  if (!opts.question) {
    throw new ProviderError('review(): question is required', { provider: PROVIDER_ID });
  }
  throw new ProviderError('ChatGPT review() is implemented in Question Factory Phase 2c.', { provider: PROVIDER_ID });
}

const chatGptProviderModule = {
  id:             PROVIDER_ID,
  displayName:    DISPLAY_NAME,
  enabled:        true,
  supportedModes: ['generate'],   // review() added in Phase 2c
  defaultModel:   DEFAULT_MODEL,
  generate,
  review,
};

export default chatGptProviderModule;
